import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

interface PositionCardProps {
  selectedPosition?: string;
  onPositionChange: (position: string) => void;
  offsetX?: number;
  offsetY?: number;
  onOffsetXChange?: (value: number) => void;
  onOffsetYChange?: (value: number) => void;
}

const POSITIONS = [
  { value: 'top-left', get label() { return translateInterfaceText("Superior Esquerda"); } },
  { value: 'top-center', get label() { return translateInterfaceText("Superior Centro"); } },
  { value: 'top-right', get label() { return translateInterfaceText("Superior Direita"); } },
  { value: 'center-left', get label() { return translateInterfaceText("Centro Esquerda"); } },
  { value: 'center', get label() { return translateInterfaceText("Centro"); } },
  { value: 'center-right', get label() { return translateInterfaceText("Centro Direita"); } },
  { value: 'bottom-left', get label() { return translateInterfaceText("Inferior Esquerda"); } },
  { value: 'bottom-center', get label() { return translateInterfaceText("Inferior Centro"); } },
  { value: 'bottom-right', get label() { return translateInterfaceText("Inferior Direita"); } },
];

export const PositionCard: React.FC<PositionCardProps> = ({
  selectedPosition = 'center',
  onPositionChange,
  offsetX = 0,
  offsetY = 0,
  onOffsetXChange,
  onOffsetYChange,
}) => {
  useLanguage();
  const showOffsets = onOffsetXChange && onOffsetYChange;

  return (
    <Card className="border-0 shadow-sm bg-[#FAFBFC]">
      <CardContent className="p-5 space-y-5">
        {/* Título da seção */}
        <Label className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Posição e Tamanho")}</Label>

        {/* Grid 3x3 Premium - Idêntico ao Logo */}
        <div className="grid grid-cols-3 gap-2">
          {POSITIONS.map((pos) => {
            const isSelected = selectedPosition === pos.value;
            return (
              <button
                key={pos.value}
                type="button"
                onClick={() => onPositionChange(pos.value)}
                className={cn(
                  "h-12 text-xs rounded-lg transition-all duration-200 ease-out font-medium",
                  "focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30",
                  isSelected
                    ? "border-2 border-[#1D4FFF] bg-[#F5F8FF] text-[#1D4FFF]"
                    : "bg-white text-[#64748B] border border-[#E6E6F0] hover:border-[#1D4FFF]/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:text-[#1D4FFF]"
                )}
              >
                {pos.label}
              </button>
            );
          })}
        </div>

        {/* Sliders de Offset - Ajustes Finos */}
        {showOffsets && (
          <div className="space-y-4 pt-2">
            <Label className="text-xs font-medium text-[#64748B]">{translateInterfaceText("Ajustes finos")}</Label>

            {/* Offset Horizontal */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748B]">{translateInterfaceText("Deslocamento Horizontal")}</span>
                <span className="text-xs font-medium text-[#0F172A] bg-[#F1F5F9] px-2 py-0.5 rounded">
                  {offsetX}px
                </span>
              </div>
              <Slider
                value={[offsetX]}
                onValueChange={([val]) => onOffsetXChange(val)}
                min={-100}
                max={100}
                step={1}
                className="w-full"
              />
            </div>

            {/* Offset Vertical */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748B]">{translateInterfaceText("Deslocamento Vertical")}</span>
                <span className="text-xs font-medium text-[#0F172A] bg-[#F1F5F9] px-2 py-0.5 rounded">
                  {offsetY}px
                </span>
              </div>
              <Slider
                value={[offsetY]}
                onValueChange={([val]) => onOffsetYChange(val)}
                min={-100}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
